const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function toDateOnly(date = new Date()): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function isDateOnly(value: string): boolean {
  if (!DATE_ONLY_PATTERN.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return false;
  const parsed = new Date(year, month - 1, day, 12);
  return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
}

export function parseDateOnly(value: string): Date {
  if (!isDateOnly(value)) throw new Error(`Invalid date: ${value}`);
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year!, month! - 1, day!, 12);
}

export function addDays(value: string, days: number): string {
  const date = parseDateOnly(value);
  date.setDate(date.getDate() + days);
  return toDateOnly(date);
}

export function formatDate(value?: string | null, emptyLabel = "—"): string {
  if (!value || !isDateOnly(value)) return emptyLabel;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parseDateOnly(value));
}

export function formatDateRange(
  startDate?: string | null,
  endDate?: string | null,
  emptyLabel = "Not set",
): string {
  if (!startDate || !endDate || !isDateOnly(startDate) || !isDateOnly(endDate)) return emptyLabel;
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  const startParts = {
    year: start.getFullYear(),
    month: start.getMonth(),
    day: start.getDate(),
  };
  const endParts = {
    year: end.getFullYear(),
    month: end.getMonth(),
    day: end.getDate(),
  };
  const month = new Intl.DateTimeFormat("en-US", { month: "short" });

  if (startParts.year === endParts.year && startParts.month === endParts.month) {
    return `${month.format(start)} ${startParts.day}–${endParts.day}, ${startParts.year}`;
  }
  if (startParts.year === endParts.year) {
    return `${month.format(start)} ${startParts.day}–${month.format(end)} ${endParts.day}, ${startParts.year}`;
  }
  return `${formatDate(startDate)}–${formatDate(endDate)}`;
}

export function nowIso(): string {
  return new Date().toISOString();
}
